/**
 * Visualizador de documentação na área administrativa.
 * Carrega a lista de docs de public/docs/manifest.json e exibe o markdown formatado.
 * Sidebar de documentos sempre visível, redimensionável, com largura persistida em localStorage.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText, Loader2, BookOpen, ChevronDown, ChevronRight } from 'lucide-react';

const DOCS_BASE = '/docs';

const manifestUrl = `${DOCS_BASE}/manifest.json`;

const SIDEBAR_STORAGE_KEY = 'admin-docs-sidebar-width';
const SIDEBAR_DEFAULT_PX = 320;
const SIDEBAR_MIN_PX = 200;
const SIDEBAR_MAX_PX = 560;

const COLLAPSED_GROUPS_STORAGE_KEY = 'admin-docs-collapsed-groups';
const LAST_DOC_PATH_STORAGE_KEY = 'admin-docs-last-path';
const SCROLL_POSITIONS_STORAGE_KEY = 'admin-docs-scroll-positions';
const SCROLL_SAVE_THROTTLE_MS = 300;

/** Monta URL do documento preservando barras (cada segmento codificado). Evita /docs/planning%2Ffile.md que quebra em SPA. */
function buildDocUrl(path) {
  const encoded = path.split('/').map((seg) => encodeURIComponent(seg)).join('/');
  return `${DOCS_BASE}/${encoded}`;
}

function DocContent({ path, onBack, onLoadComplete }) {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path) return;
    setLoading(true);
    setError(null);
    const url = buildDocUrl(path);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.text();
      })
      .then(setContent)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [path]);

  useEffect(() => {
    if (content != null && !loading && !error && onLoadComplete) {
      const id = requestAnimationFrame(() => {
        onLoadComplete();
      });
      return () => cancelAnimationFrame(id);
    }
  }, [content, loading, error, onLoadComplete]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="w-8 h-8 animate-spin mr-2" />
        <span>{t('common.loading') || 'Carregando...'}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-red-400 mb-4">{t('admin.docsLoadError') || 'Erro ao carregar documento'}: {error}</p>
        {onBack && (
          <button type="button" onClick={onBack} className="text-purple-400 hover:text-purple-300 underline">
            {t('common.back') || 'Voltar'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="admin-doc-body text-gray-200">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <h1 className="text-2xl font-bold text-white mt-6 mb-4 first:mt-0 border-b border-purple-500/30 pb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-semibold text-white mt-6 mb-3">{children}</h2>,
          h3: ({ children }) => <h3 className="text-lg font-semibold text-purple-200 mt-4 mb-2">{children}</h3>,
          h4: ({ children }) => <h4 className="text-base font-medium text-purple-200 mt-3 mb-2">{children}</h4>,
          p: ({ children }) => <p className="mb-3 leading-relaxed">{children}</p>,
          ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline break-all">
              {children}
            </a>
          ),
          code: ({ className, children, ...props }) => {
            const isBlock = className?.includes('language-');
            if (isBlock) {
              return (
                <pre className="bg-gray-900/80 border border-purple-500/20 rounded-lg p-4 overflow-x-auto mb-4 text-sm">
                  <code {...props}>{children}</code>
                </pre>
              );
            }
            return (
              <code className="bg-gray-800 px-1.5 py-0.5 rounded text-purple-200 text-sm" {...props}>
                {children}
              </code>
            );
          },
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-purple-500/50 pl-4 py-1 my-3 text-gray-400 italic">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-purple-500/20 rounded-lg border-collapse">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-purple-500/20">{children}</thead>,
          th: ({ children }) => (
            <th className="border border-purple-500/20 px-3 py-2 text-left text-sm font-semibold text-white">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-purple-500/20 px-3 py-2 text-sm">{children}</td>
          ),
          tr: ({ children }) => <tr className="border-b border-purple-500/10">{children}</tr>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          hr: () => <hr className="border-purple-500/20 my-6" />,
          strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
        }}
      >
        {content || ''}
      </ReactMarkdown>
    </div>
  );
}

function readStoredSidebarWidth() {
  try {
    const v = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (v != null) {
      const n = parseInt(v, 10);
      if (!Number.isNaN(n) && n >= SIDEBAR_MIN_PX && n <= SIDEBAR_MAX_PX) return n;
    }
  } catch (_) {}
  return SIDEBAR_DEFAULT_PX;
}

function readStoredCollapsedGroups() {
  try {
    const v = localStorage.getItem(COLLAPSED_GROUPS_STORAGE_KEY);
    if (v) {
      const arr = JSON.parse(v);
      if (Array.isArray(arr)) return new Set(arr);
    }
  } catch (_) {}
  return new Set();
}

function saveCollapsedGroups(set) {
  try {
    localStorage.setItem(COLLAPSED_GROUPS_STORAGE_KEY, JSON.stringify([...set]));
  } catch (_) {}
}

function readStoredLastDocPath() {
  try {
    const v = localStorage.getItem(LAST_DOC_PATH_STORAGE_KEY);
    return typeof v === 'string' && v.length > 0 ? v : null;
  } catch (_) {}
  return null;
}

function saveLastDocPath(path) {
  try {
    if (path) localStorage.setItem(LAST_DOC_PATH_STORAGE_KEY, path);
    else localStorage.removeItem(LAST_DOC_PATH_STORAGE_KEY);
  } catch (_) {}
}

function readScrollPositions() {
  try {
    const v = localStorage.getItem(SCROLL_POSITIONS_STORAGE_KEY);
    if (v) {
      const o = JSON.parse(v);
      if (o && typeof o === 'object') return o;
    }
  } catch (_) {}
  return {};
}

function saveScrollPosition(path, scrollTop) {
  if (path == null || typeof scrollTop !== 'number' || scrollTop < 0) return;
  try {
    const o = readScrollPositions();
    o[path] = scrollTop;
    localStorage.setItem(SCROLL_POSITIONS_STORAGE_KEY, JSON.stringify(o));
  } catch (_) {}
}

export default function AdminDocViewer({ onBack }) {
  const { t } = useTranslation();
  const [manifest, setManifest] = useState({ groups: [], documents: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [sidebarWidthPx, setSidebarWidthPx] = useState(readStoredSidebarWidth);
  const [collapsedGroups, setCollapsedGroups] = useState(readStoredCollapsedGroups);
  const [resizing, setResizing] = useState(false);
  const selectedItemRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, width: 0 });
  const scrollContainerRef = useRef(null);
  const scrollSaveTimeoutRef = useRef(null);

  const toggleGroup = useCallback((groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      saveCollapsedGroups(next);
      return next;
    });
  }, []);

  useEffect(() => {
    fetch(manifestUrl)
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((data) => {
        const docs = Array.isArray(data) ? data : (data.documents || []);
        const groups = Array.isArray(data) ? [] : (data.groups || []);
        setManifest({ groups, documents: docs });
        const lastPath = readStoredLastDocPath();
        if (lastPath && docs.some((d) => d.path === lastPath)) {
          setSelectedPath(lastPath);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedPath) saveLastDocPath(selectedPath);
  }, [selectedPath]);

  const handleDocScroll = useCallback(() => {
    const path = selectedPath;
    const el = scrollContainerRef.current;
    if (!path || !el) return;
    const scrollTop = el.scrollTop;
    if (scrollSaveTimeoutRef.current != null) clearTimeout(scrollSaveTimeoutRef.current);
    scrollSaveTimeoutRef.current = setTimeout(() => {
      scrollSaveTimeoutRef.current = null;
      saveScrollPosition(path, scrollTop);
    }, SCROLL_SAVE_THROTTLE_MS);
  }, [selectedPath]);

  const handleDocLoadComplete = useCallback(() => {
    const path = selectedPath;
    const el = scrollContainerRef.current;
    if (!path || !el) return;
    const positions = readScrollPositions();
    const saved = positions[path];
    if (typeof saved === 'number' && saved >= 0) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = saved;
      });
    }
  }, [selectedPath]);

  useEffect(() => {
    return () => {
      if (scrollSaveTimeoutRef.current) clearTimeout(scrollSaveTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (selectedPath && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedPath]);

  const saveSidebarWidth = useCallback((px) => {
    const clamped = Math.min(SIDEBAR_MAX_PX, Math.max(SIDEBAR_MIN_PX, px));
    setSidebarWidthPx(clamped);
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(clamped));
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const x = typeof e.clientX === 'number' ? e.clientX : e.touches?.[0]?.clientX;
      if (typeof x !== 'number') return;
      const { x: startX, width: startWidth } = resizeStartRef.current;
      saveSidebarWidth(startWidth + (x - startX));
    };
    const onEnd = () => setResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [resizing, saveSidebarWidth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="w-8 h-8 animate-spin mr-2" />
        <span>{t('common.loading') || 'Carregando...'}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-red-400 mb-4">{t('admin.docsListError') || 'Erro ao carregar lista de documentos'}: {error}</p>
        <p className="text-sm text-gray-500 mb-4">
          {t('admin.docsRunPrebuild') || 'Execute npm run dev ou npm run build para gerar public/docs/.'}
        </p>
        {onBack && (
          <button type="button" onClick={onBack} className="text-purple-400 hover:text-purple-300 underline">
            {t('common.back') || 'Voltar'}
          </button>
        )}
      </div>
    );
  }

  const { groups, documents } = manifest;
  const groupOrder =
    groups.length > 0 ? groups : [...new Set(documents.map((d) => d.group).filter(Boolean))];
  if (groupOrder.length === 0 && documents.length > 0) {
    groupOrder.push('other');
  }
  const byGroup = groupOrder.reduce((acc, key) => {
    acc[key] = key === 'other' ? documents : documents.filter((d) => d.group === key);
    return acc;
  }, {});

  return (
    <div className="flex flex-1 min-h-0 w-full">
      {/* Sidebar: rolagem independente, grupos expansíveis */}
      <div
        className="flex-shrink-0 flex flex-col min-h-0 border border-purple-500/20 rounded-lg bg-gray-800/50"
        style={{ width: sidebarWidthPx }}
      >
        <div className="flex items-center gap-2 text-white font-semibold p-4 pb-2 flex-shrink-0">
          <BookOpen className="w-5 h-5 text-purple-400 flex-shrink-0" />
          <span>{t('admin.docsTitle') || 'Documentação do projeto'}</span>
        </div>
        <p className="text-sm text-gray-400 px-4 pb-3 flex-shrink-0">
          {t('admin.docsIntro') || 'Selecione um documento para visualizar.'}
        </p>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 pb-4 pr-2">
          <div className="space-y-4">
            {groupOrder.map((groupKey) => {
              const items = byGroup[groupKey] || [];
              if (items.length === 0) return null;
              const groupLabel = t(`admin.docsGroup_${groupKey}`) || groupKey;
              const isExpanded = !collapsedGroups.has(groupKey);
              return (
                <div key={groupKey}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupKey)}
                    className="w-full flex items-center gap-2 text-left text-xs font-semibold text-purple-300 uppercase tracking-wider py-2 rounded hover:bg-purple-500/10 transition-colors"
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span className="truncate">{groupLabel}</span>
                    <span className="text-gray-500 font-normal normal-case ml-1">({items.length})</span>
                  </button>
                  {isExpanded && (
                    <ul className="space-y-1 mt-0">
                      {items.map(({ path: docPath, title }) => {
                        const isSelected = selectedPath === docPath;
                        return (
                          <li key={docPath}>
                            <button
                              type="button"
                              ref={isSelected ? selectedItemRef : undefined}
                              onClick={() => setSelectedPath(docPath)}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg transition-colors ${
                                isSelected
                                  ? 'bg-purple-600/60 text-white border border-purple-400/50'
                                  : 'text-gray-300 hover:bg-purple-500/20 hover:text-white border border-transparent'
                              }`}
                            >
                              <FileText className="w-4 h-4 flex-shrink-0 text-purple-400" />
                              <span className="truncate">{title}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Divisor redimensionável */}
      <button
        type="button"
        aria-label={t('admin.docsResizeSidebar') || 'Redimensionar menu de documentos'}
        className={`flex-shrink-0 w-2 flex items-center justify-center cursor-col-resize hover:bg-purple-500/20 transition-colors ${resizing ? 'bg-purple-500/30' : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          resizeStartRef.current = { x: e.clientX, width: sidebarWidthPx };
          setResizing(true);
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          const x = e.touches?.[0]?.clientX;
          if (typeof x === 'number') resizeStartRef.current = { x, width: sidebarWidthPx };
          setResizing(true);
        }}
      >
        <span className="w-0.5 h-8 bg-purple-500/50 rounded-full" />
      </button>

      {/* Área de conteúdo: rolagem independente do menu; posição de rolagem persistida */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col rounded-lg border border-purple-500/20 bg-gray-900/40 overflow-hidden">
        {selectedPath ? (
          <div
            ref={scrollContainerRef}
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-6"
            onScroll={handleDocScroll}
          >
            <DocContent
              path={selectedPath}
              onBack={null}
              onLoadComplete={handleDocLoadComplete}
            />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm p-6 min-h-0">
            {t('admin.docsSelectPrompt') || 'Selecione um documento na lista'}
          </div>
        )}
      </div>
    </div>
  );
}
